import { useEffect, type ReactNode } from "react";

import { PageLoader } from "@/components/PageLoader";
import { useCurrentUser } from "@/features/identity/hooks/useCurrentUser";
import { getAccessToken } from "@/lib/token-storage";
import { useAppDispatch } from "@/store/hooks";
import { setCredentials } from "@/store/auth/authSlice";

/**
 * The access token persists across a page reload (localStorage —
 * src/lib/token-storage.ts); `state.auth` does not (Redux is in-memory
 * only). Without this, a hard reload/new-tab always resets `auth.status`
 * to `"guest"` regardless of a perfectly valid stored token, and
 * `RequireAuth` (src/routes/guards/RequireAuth.tsx) would incorrectly
 * bounce an actually-logged-in user to `/login` — a bug that only showed
 * up once a real backend existed to reload against, not during earlier
 * development against no backend at all.
 *
 * Mounted once, above the router (src/App.tsx): if a token exists, holds
 * rendering behind a `PageLoader` until `GET /users/me` confirms it and
 * repopulates `state.auth` — so route guards never run against a
 * not-yet-rehydrated state. A guest with no token renders immediately,
 * no gate at all.
 */
export function SessionBootstrap({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const hasToken = Boolean(getAccessToken());
  const currentUserQuery = useCurrentUser();

  useEffect(() => {
    if (currentUserQuery.data) {
      dispatch(setCredentials({ user: currentUserQuery.data }));
    }
  }, [currentUserQuery.data, dispatch]);

  if (hasToken && currentUserQuery.isPending) {
    return <PageLoader />;
  }

  return children;
}
